import api from '@/lib/api';
import type { SimpleCompletionSummary } from '@/pages/eduflow/simple/simple-player-completion';

export type SimpleChapter = {
  id: string;
  moduleId: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  blockIds: string[];
  hasHomeworkBlock?: boolean;
};

export type SimpleModule = {
  id: string;
  flowId: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  unlockDelayDays?: number;
  chapters: SimpleChapter[];
};

export type SimpleFormationDescriptionAudio = {
  ttsEnabled?: boolean;
  mistralVoiceId?: string;
  ttsSpeed?: number;
  ttsLanguage?: string;
  descriptionAudioUrl?: string | null;
  descriptionAudioLang?: string | null;
  ttsSignature?: string;
};

export type SimpleStructure = {
  flow: {
    id: string;
    title: string;
    description?: string | null;
    descriptionAudio?: SimpleFormationDescriptionAudio | null;
    status: string;
    creationMode: string;
  };
  modules: SimpleModule[];
};

export type SimpleUndoKind = 'structure' | 'chapter_blocks' | 'ai' | 'ai_generate';

export type SimpleUndoEntry = {
  id: string;
  kind: SimpleUndoKind;
  label: string;
  chapterId?: string | null;
  createdAt: string;
};

export type SimpleBlockPayload = {
  id: string;
  type: 'text' | 'title' | 'subtitle' | 'image' | 'video' | 'audio' | 'quiz_mcq' | 'quiz_open' | 'web_links' | 'homework';
  title?: string | null;
  data?: Record<string, unknown>;
};

export type SimplePlayerPayload = {
  flow: {
    id: string;
    title: string;
    description?: string | null;
    status: string;
    creationMode: 'simple';
    language?: string | null;
    coverImageUrl?: string | null;
  };
  modules: Array<{
    id: string;
    title: string;
    description?: string | null;
    sortOrder: number;
    unlockDelayDays?: number;
    chapters: Array<{
      id: string;
      title: string;
      description?: string | null;
      sortOrder: number;
      blockCount: number;
      dripLocked?: boolean;
      dripUnlocksAt?: string | null;
    }>;
  }>;
  activeChapterId: string | null;
  activeBlockId?: string | null;
  completedChapterIds?: string[];
  blockResponses?: Record<string, unknown>;
  totalTimeSpent?: number;
  enrolledAt?: string | null;
  dripEnabled?: boolean;
  blocks: Array<{
    id: string;
    type: string;
    title?: string | null;
    position: { x: number; y: number };
    data: Record<string, unknown>;
    order: number;
    connections: { inputs: string[]; outputs: string[] };
    isRequired: boolean;
    isLocked: boolean;
  }>;
  institutionBranding: unknown;
  translationSourceLanguage?: string;
  localeMetaPending?: boolean;
  contentReloadInstant?: boolean;
  skipPlayerBackgroundWarm?: boolean;
  structureLocalePending?: boolean;
  gamification?: Record<string, unknown>;
  objectives?: Array<{
    id?: string;
    text: string;
    category?: 'savoir' | 'savoir-faire' | 'savoir-être';
    level?: 'connaissance' | 'compréhension' | 'application' | 'analyse' | 'synthèse' | 'évaluation';
    required?: boolean;
    linkedBlocks?: Array<{ blockId: string; criterion?: 'completed' | 'score'; minScore?: number }>;
  }>;
};

export type SimplePlayerKind = {
  id: string;
  creationMode: 'simple' | 'advanced';
};

function unwrap<T>(resp: unknown): T {
  const r = resp as { data?: T };
  return (r?.data ?? resp) as T;
}

export type SimplePlayerFetchOptions = {
  chapterId?: string | null;
  lang?: string | null;
  prioritizeBlockId?: string | null;
  preview?: boolean;
};

function playParams(opts?: SimplePlayerFetchOptions) {
  const params: Record<string, string> = {};
  if (opts?.chapterId) params.chapterId = opts.chapterId;
  if (opts?.lang) params.lang = opts.lang;
  if (opts?.prioritizeBlockId) params.prioritizeBlock = opts.prioritizeBlockId;
  if (opts?.preview) params.preview = '1';
  return Object.keys(params).length ? params : undefined;
}

export type SimpleAiAppliedSummary = {
  moduleCount: number;
  chapterCount: number;
  chaptersWithContent: number;
  blockCount: number;
  objectiveCount: number;
  structureUpdated: boolean;
  objectivesUpdated: boolean;
  gamificationUpdated?: boolean;
};

export type SimpleAiChatResult = {
  message: string;
  applied: boolean;
  appliedSummary?: SimpleAiAppliedSummary;
  structure?: SimpleStructure | null;
  gamification?: Record<string, unknown> | null;
  followUpPrompts?: string[];
};

export type SimpleAiBlockOutline = {
  type: 'text' | 'title' | 'subtitle' | 'image' | 'video' | 'audio' | 'quiz_mcq' | 'quiz_open';
  title?: string | null;
  summary?: string | null;
};

export type SimpleAiPlanOutline = {
  title?: string;
  description?: string | null;
  objectives?: SimpleAiStructureSnapshot['objectives'];
  modules: Array<{
    title: string;
    description?: string | null;
    chapters: Array<{
      title: string;
      description?: string | null;
      blocks?: SimpleAiBlockOutline[];
    }>;
  }>;
};

export type SimpleAiPlanResult = {
  message: string;
  plan: SimpleAiPlanOutline | null;
  followUpPrompts?: string[];
};

export type SimpleAiGenerateResult = {
  message: string;
  applied: boolean;
  appliedSummary?: SimpleAiAppliedSummary;
  structure?: SimpleStructure | null;
  pendingChapters?: Array<{
    moduleIndex: number;
    chapterIndex: number;
    moduleTitle: string;
    chapterTitle: string;
  }>;
};

export type SimpleAiGenerateChapterResult = {
  message: string;
  moduleIndex: number;
  chapterIndex: number;
  blockCount: number;
  done: boolean;
  totalChapters: number;
  completedChapters: number;
};

export type SimpleAiJob = {
  id: string;
  flowId: string;
  flowTitle: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  structureReady: boolean;
  completedChapters: number;
  totalChapters: number;
  currentChapterLabel: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SimpleAiStructureSnapshot = {
  title: string;
  description?: string | null;
  objectives?: Array<{
    id?: string;
    text: string;
    category?: 'savoir' | 'savoir-faire' | 'savoir-être';
    level?: 'connaissance' | 'compréhension' | 'application' | 'analyse' | 'synthèse' | 'évaluation';
    required?: boolean;
    linkedBlocks?: Array<{ blockId: string; criterion?: 'completed' | 'score'; minScore?: number }>;
  }>;
  gamification?: Record<string, unknown> | null;
  modules: Array<{
    id?: string;
    title: string;
    chapters: Array<{
      id?: string;
      title: string;
      blocks?: Array<{
        id?: string;
        type: string;
        title?: string | null;
        content?: string | null;
        audioMode?: string | null;
        data?: Record<string, unknown>;
      }>;
    }>;
  }>;
};

export const eduflowSimpleApi = {
  getStructure: (flowId: string) =>
    api.get<SimpleStructure>(`/eduflows/${flowId}/simple/structure`).then(unwrap),

  saveStructure: (flowId: string, payload: {
    title?: string;
    description?: string | null;
    descriptionAudio?: SimpleFormationDescriptionAudio | null;
    modules: Array<{
      id?: string;
      title: string;
      description?: string | null;
      sortOrder: number;
      unlockDelayDays?: number;
      chapters: Array<{
        id?: string;
        title: string;
        description?: string | null;
        sortOrder: number;
      }>;
    }>;
  }) =>
    api.put<SimpleStructure>(`/eduflows/${flowId}/simple/structure`, payload).then(unwrap),

  aiChat: (
    flowId: string,
    payload: {
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
      structureSnapshot: SimpleAiStructureSnapshot;
      focusChapterId?: string;
      focusBlockId?: string;
      focusBlockTitle?: string;
      documentText?: string;
      documentName?: string;
    },
  ) =>
    api.post<SimpleAiChatResult>(`/eduflows/${flowId}/simple/ai-chat`, payload, { timeout: 120_000 }).then(unwrap),

  aiPlan: (
    flowId: string,
    payload: {
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
      structureSnapshot: SimpleAiStructureSnapshot;
      documentText?: string;
      documentName?: string;
      currentPlan?: SimpleAiPlanOutline | null;
    },
  ) =>
    api.post<SimpleAiPlanResult>(`/eduflows/${flowId}/simple/ai-plan`, payload, { timeout: 120_000 }).then(unwrap),

  aiGenerate: (
    flowId: string,
    payload: {
      plan: SimpleAiPlanOutline;
      structureSnapshot: SimpleAiStructureSnapshot;
      documentText?: string;
    },
  ) =>
    api.post<SimpleAiGenerateResult>(`/eduflows/${flowId}/simple/ai-generate`, payload, { timeout: 120_000 }).then(unwrap),

  aiGenerateChapter: (
    flowId: string,
    payload: {
      plan: SimpleAiPlanOutline;
      structureSnapshot: SimpleAiStructureSnapshot;
      moduleIndex: number;
      chapterIndex: number;
      documentText?: string;
    },
  ) =>
    api
      .post<SimpleAiGenerateChapterResult>(`/eduflows/${flowId}/simple/ai-generate-chapter`, payload, { timeout: 180_000 })
      .then(unwrap),

  startAiJob: (
    flowId: string,
    payload: {
      plan: SimpleAiPlanOutline;
      structureSnapshot: SimpleAiStructureSnapshot;
      documentText?: string;
    },
  ) =>
    api.post<SimpleAiJob>(`/eduflows/${flowId}/simple/ai-jobs`, payload).then(unwrap),

  listAiJobs: (flowId: string) =>
    api
      .get<{ jobs: SimpleAiJob[] }>(`/eduflows/${flowId}/simple/ai-jobs`)
      .then((resp) => unwrap<{ jobs: SimpleAiJob[] }>(resp).jobs),

  getAiJob: (flowId: string, jobId: string) =>
    api.get<SimpleAiJob>(`/eduflows/${flowId}/simple/ai-jobs/${jobId}`).then(unwrap),

  listActiveAiJobs: () =>
    api
      .get<{ jobs: SimpleAiJob[] }>('/eduflows/simple/ai-jobs/active')
      .then((resp) => unwrap<{ jobs: SimpleAiJob[] }>(resp).jobs),

  getChapter: (flowId: string, chapterId: string) =>
    api.get<{
      chapter: SimpleChapter & { module: { id: string; title: string } };
      blocks: SimpleBlockPayload[];
      structure: SimpleStructure;
    }>(`/eduflows/${flowId}/simple/chapters/${chapterId}`).then(unwrap),

  saveChapterBlocks: (
    flowId: string,
    chapterId: string,
    payload: {
      blocks: SimpleBlockPayload[];
      title?: string;
      description?: string | null;
    },
  ) =>
    api.put(`/eduflows/${flowId}/simple/chapters/${chapterId}`, payload).then(unwrap),

  compile: (flowId: string) =>
    api.post(`/eduflows/${flowId}/simple/compile`).then(unwrap),

  getPlayerKind: (flowId: string) =>
    api.get<SimplePlayerKind>(`/eduflows/${flowId}/player-kind`).then(unwrap),

  getPlayerKindByPublicCode: (code: string) =>
    api.get<SimplePlayerKind>(`/eduflows/public/code/${code}/player-kind`).then(unwrap),

  getPlayerPlay: (flowId: string, opts?: SimplePlayerFetchOptions) =>
    api.get<SimplePlayerPayload>(
      `/eduflows/${flowId}/simple/play`,
      playParams(opts),
      { timeout: 120_000 },
    ).then(unwrap),

  getPublicPlayerPlay: (flowId: string, opts?: SimplePlayerFetchOptions) =>
    api.get<SimplePlayerPayload>(
      `/eduflows/${flowId}/simple/public/play`,
      playParams(opts),
      { timeout: 120_000 },
    ).then(unwrap),

  getPublicPlayerPlayByCode: (code: string, opts?: SimplePlayerFetchOptions) =>
    api.get<SimplePlayerPayload>(
      `/eduflows/public/code/${code}/simple/play`,
      playParams(opts),
      { timeout: 120_000 },
    ).then(unwrap),

  recordPlayTime: (
    flowId: string,
    elapsedSeconds: number,
    opts?: { blockId?: string | null; chapterId?: string | null },
  ) =>
    api
      .post<{ totalTimeSpent: number }>(`/eduflows/${flowId}/simple/play/time`, {
        elapsedSeconds,
        ...(opts?.blockId ? { blockId: opts.blockId } : {}),
        ...(opts?.chapterId ? { chapterId: opts.chapterId } : {}),
      })
      .then(unwrap),

  saveBlockResponse: (
    flowId: string,
    blockId: string,
    payload: {
      response: unknown;
      score?: number;
      maxScore?: number;
      chapterId?: string;
    },
  ) =>
    api
      .post<{ blockId: string; saved: boolean }>(
        `/eduflows/${flowId}/simple/blocks/${blockId}/response`,
        payload,
      )
      .then(unwrap),

  scoreOpenQuestion: (flowId: string, blockId: string, learnerAnswer: string) =>
    api
      .post<{
        score: number;
        passed: boolean;
        rationale: string;
        threshold: number;
        gradingMode: 'semantic' | 'exact' | 'ungraded';
        pointsEarned: number;
        questionPoints: number;
      }>(`/eduflows/${flowId}/simple/blocks/${blockId}/semantic-score`, { learnerAnswer })
      .then(unwrap),

  listHomeworkSubmissions: (flowId: string, blockId: string): Promise<{
    blockId: string;
    blockTitle: string;
    submissions: Array<{
      studentId: string;
      learnerName: string;
      learnerEmail: string;
      submittedAt: string;
      response: Record<string, unknown>;
      trainerStatus: 'pending' | 'graded' | 'returned';
      trainerFeedback: string | null;
      trainerScore: number | null;
      gradedAt: string | null;
    }>;
  }> =>
    api
      .get<{
        blockId: string;
        blockTitle: string;
        submissions: Array<{
          studentId: string;
          learnerName: string;
          learnerEmail: string;
          submittedAt: string;
          response: Record<string, unknown>;
          trainerStatus: 'pending' | 'graded' | 'returned';
          trainerFeedback: string | null;
          trainerScore: number | null;
          gradedAt: string | null;
        }>;
      }>(`/eduflows/${flowId}/simple/homework/${blockId}/submissions`)
      .then((r) => unwrap(r)),

  gradeHomeworkSubmission: (
    flowId: string,
    blockId: string,
    studentId: string,
    payload: {
      trainerStatus: 'pending' | 'graded' | 'returned';
      trainerFeedback?: string;
      trainerScore?: number | null;
    },
  ) =>
    api
      .patch<{ studentId: string; blockId: string; saved: boolean }>(
        `/eduflows/${flowId}/simple/homework/${blockId}/submissions/${studentId}`,
        payload,
      )
      .then(unwrap),

  getCompletionSummary: (flowId: string) =>
    api
      .get<SimpleCompletionSummary>(`/eduflows/${flowId}/simple/completion-summary`)
      .then(unwrap),

  listUndo: (flowId: string): Promise<{ entries: SimpleUndoEntry[] }> =>
    api
      .get<{ entries: SimpleUndoEntry[] }>(`/eduflows/${flowId}/simple/undo`)
      .then((r) => unwrap<{ entries: SimpleUndoEntry[] }>(r)),

  restoreUndo: (flowId: string, entryId: string): Promise<{ structure: SimpleStructure }> =>
    api
      .post<{ structure: SimpleStructure }>(`/eduflows/${flowId}/simple/undo/${entryId}/restore`)
      .then((r) => unwrap<{ structure: SimpleStructure }>(r)),

  seedUndoBaseline: (flowId: string, chapterId: string): Promise<{ created: boolean }> =>
    api
      .post<{ created: boolean }>(`/eduflows/${flowId}/simple/undo/baseline`, { chapterId })
      .then((r) => unwrap<{ created: boolean }>(r)),
};

export function getEduFlowEditPath(flow: { id: string; creationMode?: string | null }): string {
  if (flow.creationMode === 'simple') return `/eduflow/simple/${flow.id}/structure`;
  return `/eduflow/edit/${flow.id}`;
}
